import {useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router-dom";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {TextInput} from "../../Components/Form/TextInput";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {PasswordInput} from "../../Components/Form/PasswordInput";
import {LanguageSelector} from "../../Components/Form/LanguageSelector";
import {useState} from "react";
import check from '../../Assets/images/check.png';
import {useBoundStore} from "../../Store/store";
import Logo from '../../Assets/images/logo/connectcaremintgreen.png';
import * as React from "react";
import {TosCustomerUnstyled} from "../../Components/Legal/TosCustomer";
import {ControlledSwitch} from "../../Components/Form/ControlledSwitch";
import {SelectInput} from "../../Components/Form/SelectInput";
import {prepareConsentData} from "../../Utils/ConsentTracker";
import {Switch} from "@headlessui/react";

export const RegisterCustomer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const registerCustomer = useBoundStore(state => state.registerCustomer);

    const [currentStep, setCurrentStep] = useState(1);
    const login = useBoundStore((state) => state.login);
    const logOut = useBoundStore((state) => state.logOut);
    const user = useBoundStore((state) => state.user);

    const schema = yup
        .object({
            first_name: yup.string().required(t('general.errors.first_name_empty')),
            last_name: yup.string().required(t('general.errors.last_name_empty')),
            email: yup.string().email().required(t('general.errors.email_empty')),
            tel: yup.string(),
            healthcare_institution: yup.string().required(t('general.errors.healthcare_institution_empty')),
            vat_number: yup.string().required(t('general.errors.vat_empty')),
            street: yup.string().required(t('general.errors.street_empty')),
            house_number: yup.string().required(t('general.errors.number_empty')),
            bus: yup.string(),
            zip: yup.string().required(),
            city: yup.string().required(),
            province: yup.string().required(t('general.errors.province_empty')).oneOf(['antwerpen', 'limburg', 'oost-vlaanderen', 'vlaams-brabant', 'west-vlaanderen','henegouwen','luik','luxemburg','namen','waals-brabant','brussels-hoofdstedelijk-gewest']),
            bank_account_number: yup.string().nullable(),
            password: yup.string().min(8)
                .required(t('general.errors.password_empty'))
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
                    t('login.password_requirement')
                ),
            password_confirmation: yup.string().oneOf([yup.ref('password'), null], t('general.errors.password_no_match')).required(t('general.errors.password_empty')),
            agree_with_contract: yup.boolean().oneOf([true], t('general.errors.contract_not_accepted')),
            agree_with_terms: yup.boolean().oneOf([true], t('general.errors.tos_not_accepted')),
            agree_with_privacy: yup.boolean().oneOf([true], t('yup_errors.privacy_not_accepted'))
        })
        .required()

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        control,
        setValue,
        getValues
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues: {
            locale: 'nl',
        }
    })

    const onSubmit = async (data) => {
        await trigger(["agree_with_contract", "agree_with_terms", "agree_with_privacy"], {shouldFocus: true})
            .then(async bool => bool ?
                await registerCustomer({
                    ...data,
                    ...prepareConsentData(data.agree_with_terms, data.agree_with_privacy)
                })
                    .then((response) => {
                        if (response.status === 201 || response.status === 200) {
                            toast.success(t('register_customer.toast_success'), {
                                duration: 5000,
                                position: 'top-center',
                                style: {
                                    zIndex: 9999,
                                }
                            });

                            setTimeout(() => {
                                logOut();
                                login(data.email, data.password).then(() => {
                                    setCurrentStep(currentStep + 1);
                                });
                            }, 1000);
                        } else {
                            if(response.response.status === 422 && currentStep !== 3) {
                                toast.error(response?.response?.data?.message ?? t('register_customer.toast_error'));
                            }
                        }
                    })
                    .catch((error) => {
                        toast.error(error?.response?.data?.message ?? t('register_customer.toast_error'));
                        console.error('Error creating customer', error);
                    })
            :
                false
            )
    }

    const progressSteps = () => {
        if (currentStep === 1) {
            trigger(["email", "phone", "healthcare_institution", "vat_number", "street", "nr", "bus", "zip", "city",
                "bank_account_number", "password", "confirm_password", "first_name","last_name", "province"], {shouldFocus: true})
                .then(bool => bool ? setCurrentStep(2) : false)
        }
        if (currentStep === 3 && user) {
            navigate('/customer/profile')
        }
    }

    return (
        <div className={'flex flex-col justify-center min-h-screen w-full h-full'}>
            <div className={'p-3 text-center'}>
                <img src={Logo} alt={'Logo for connect care mint green'} className={'max-h-16 mx-auto'}/>
            </div>
            <div className={'p-3 w-full max-w-[80%] md:max-w-[70%] mx-auto'}>
                <h1 className={'text-2xl font-bold text-center'}>{t('register_page.title')}</h1>
                <p className={'text-center text-sm text-gray-500 mb-4'}>
                    {t('register_page.subtitle')}
                </p>

                <p className={'text-sm text-center text-gray-500'}>{t('register_page.have_account')}
                <Link
                    to={'/login'}
                    className={'text-primary hover:underline ml-1 text-sm text-center'}
                >
                    {t('register_page.back_to_login')}
                </Link>
                </p>
            </div>
            <BaseCard>
                <div className={'w-full flex flex-col justify-center xl:min-w-[700px]'}>
                    <ul className="relative flex flex-row gap-x-2  justify-center">
                        <li className="flex items-center gap-x-2 -gap-r-1 shrink basis-0 flex-1 group">
                            <div
                                className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                            <span
                                className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep > 1 ? 'finishedStep' : ''} ${currentStep === 1 ? 'activeStep' : ''}`}>1</span>
                                <span className="ms-2 block text-sm font-medium text-gray-800">{t('register_page.step_info')}</span>
                            </div>
                            <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                        </li>

                        <li className="flex items-center gap-x-2 shrink basis-0 flex-1 group">
                            <div
                                className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                            <span
                                className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep > 2 ? 'finishedStep' : ''} ${currentStep === 2 ? 'activeStep' : ''}`}>2</span>
                                <span className="ms-2 block text-sm font-medium text-gray-800">{t('auth.register_customer.step_contract')}</span>
                            </div>
                            <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                        </li>
                        <li className="flex items-center gap-x-2 shrink basis-0 flex-1 group">
                            <div
                                className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                            <span
                                className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep === 3 ? 'activeStep' : ''}`}>3</span>
                                <span className="ms-2 block text-sm font-medium text-gray-800">{t('register_page.step_confirm')}</span>
                            </div>
                            <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                        </li>
                    </ul>
                </div>
                <hr className="mt-4 mb-4 border-gray-200"/>
                <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                    {
                        currentStep === 1 && (
                            <>
                                {/* CCPSOM-30: language selector disabled until multi-language support is ready
                                <div className="mb-4 max-w-xs">
                                    <LanguageSelector
                                        label={t('language.label')}
                                        value={getValues('locale')}
                                        onChange={(loc) => setValue('locale', loc)}
                                    />
                                </div> */}
                                <div className={'grid grid-cols-1 md:grid-cols-2 mb-4 gap-4'}>
                                    <TextInput
                                        label={t('general.fields.first_name')}
                                        name={'first_name'}
                                        register={register}
                                        translationIndex={'general.fields.firstname'}
                                        errors={errors}
                                    />

                                    <TextInput
                                        label={t('general.fields.last_name')}
                                        name={'last_name'}
                                        register={register}
                                        translationIndex={'general.fields.lastname'}
                                        errors={errors}
                                    />
                                </div>

                                <div className={'grid grid-cols-1 md:grid-cols-2 mb-4 gap-4'}>
                                    <TextInput
                                        label={t('general.fields.email')}
                                        name={'email'}
                                        register={register}
                                        translationIndex={'general.fields.email'}
                                        errors={errors}
                                    />

                                    <TextInput
                                        label={'Phone'}
                                        name={'tel'}
                                        register={register}
                                        translationIndex={'general.fields.tel'}
                                        errors={errors}
                                    />
                                </div>

                                <div className={'grid grid-cols-1 md:grid-cols-2 mb-4 gap-4'}>
                                    <PasswordInput
                                        label={'Password'}
                                        name={'password'}
                                        register={register}
                                        translationIndex={'general.fields.password'}
                                        errors={errors}
                                    />

                                    <PasswordInput
                                        label={'Confirm password'}
                                        name={'password_confirmation'}
                                        register={register}
                                        translationIndex={'general.fields.confirm_password'}
                                        errors={errors}
                                    />
                                </div>

                                <div className={'grid grid-cols-1 md:grid-cols-3 mb-4 gap-4'}>
                                    <TextInput
                                        label={'Street'}
                                        name={'street'}
                                        register={register}
                                        translationIndex={'general.fields.street'}
                                        errors={errors}
                                    />

                                    <TextInput
                                        label={'Nr.'}
                                        name={'house_number'}
                                        register={register}
                                        translationIndex={'general.fields.nr'}
                                        errors={errors}
                                    />

                                    <TextInput
                                        label={'Bus'}
                                        name={'bus'}
                                        register={register}
                                        translationIndex={'general.fields.bus'}
                                        errors={errors}
                                    />
                                </div>
                                <div className={'grid grid-cols-1 md:grid-cols-3 mb-4 gap-4'}>
                                    <TextInput
                                        label={'Zip code'}
                                        name={'zip'}
                                        register={register}
                                        translationIndex={'general.fields.postal_code'}
                                        errors={errors}
                                    />

                                    <TextInput
                                        label={'City'}
                                        name={'city'}
                                        register={register}
                                        translationIndex={'general.fields.city'}
                                        errors={errors}
                                    />

                                    <SelectInput
                                        name={'province'}
                                        options={[
                                            {id: 1, value: 'antwerpen', 'label': t('provinces.antwerp')},
                                            {id: 2, value: 'limburg', 'label': t('provinces.limburg')},
                                            {id: 3, value: 'oost-vlaanderen', 'label': t('provinces.east_flanders')},
                                            {id: 4, value: 'vlaams-brabant', 'label': t('provinces.flemish_brabant')},
                                            {id: 5, value: 'west-vlaanderen', 'label': t('provinces.west_flanders')},
                                            {id: 6, value: 'henegouwen', 'label': t('provinces.hainaut')},
                                            {id: 7, value: 'luik', 'label': t('provinces.liege')},
                                            {id: 8, value: 'luxemburg', 'label': t('provinces.luxembourg')},
                                            {id: 9, value: 'namen', 'label': t('provinces.namur')},
                                            {id: 10, value: 'waals-brabant', 'label': t('provinces.walloon_brabant')},
                                            {id: 11, value: 'brussels-hoofdstedelijk-gewest', 'label': t('provinces.brussels')}
                                        ]}
                                        setValue={setValue}
                                        control={control}
                                        value={getValues('province') ?? null}
                                        placeholder={t('general.fields.province')}
                                        translationIndex={'general.fields.province'}
                                        errors={errors}
                                    />
                                </div>

                                <div className={'grid grid-cols-1 md:grid-cols-2 mb-4 gap-4'}>
                                    <TextInput
                                        label={'Healthcare institution'}
                                        name={'healthcare_institution'}
                                        register={register}
                                        translationIndex={'general.fields.healthcare_instiution'}
                                        errors={errors}
                                    />

                                    <TextInput
                                        label={'Vat number'}
                                        name={'vat_number'}
                                        register={register}
                                        translationIndex={'general.fields.vat_number'}
                                        errors={errors}
                                    />
                                </div>

                                <div className={'mt-4 mb-4'}>
                                    <TextInput
                                        label={'Bank account number'}
                                        name={'bank_account_number'}
                                        register={register}
                                        translationIndex={'general.fields.bank_account_number'}
                                        errors={errors}
                                    />
                                </div>
                            </>
                        )
                    }

                    {
                        currentStep === 2 && (
                            <div>
                                <h2 className={'text-xl font-bold mb-4'}>{t('auth.register_customer.step_contract')}</h2>

                                <div
                                    className={'max-h-64 overflow-auto w-full p-3 my-1 bg-gray-100 text-gray-500 rounded-md '}
                                    disabled={true}>
                                    <TosCustomerUnstyled/>
                                </div>

                                <div className={'my-4'}>
                                    <ControlledSwitch
                                        control={control}
                                        name={'agree_with_contract'}
                                        getValues={getValues}
                                        setValue={setValue}
                                        errors={errors}
                                        translationIndex={'general.fields.contract_accept'}
                                    />
                                </div>

                                <div className={'my-4'}>
                                    <ControlledSwitch
                                        control={control}
                                        name={'agree_with_terms'}
                                        getValues={getValues}
                                        setValue={setValue}
                                        errors={errors}
                                        translationIndex={'general.fields.tos_accept'}
                                    />
                                </div>

                                <div className={'my-4'}>
                                    <div className="flex flex-row">
                                        <Controller
                                            control={control}
                                            defaultValue={false}
                                            name={'agree_with_privacy'}
                                            rules={{required: true}}
                                            render={({field: {ref, ...rest}}) => (
                                                <Switch
                                                    {...rest}
                                                    checked={getValues('agree_with_privacy')}
                                                    onChange={() => {
                                                        setValue('agree_with_privacy', !getValues('agree_with_privacy'))
                                                    }}
                                                    value={getValues('agree_with_privacy')}
                                                    className={`${
                                                        getValues('agree_with_privacy') ? 'bg-primary' : 'bg-gray-200'
                                                    } relative inline-flex h-6 w-11 items-center rounded-full`}
                                                >
                                                    <span className="sr-only">{t('register_page.privacy_switch_sr')}</span>
                                                    <span
                                                        className={`${getValues('agree_with_privacy') ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                                    />
                                                </Switch>
                                            )}
                                        />
                                        <p className="pl-2">
                                            {t('register_page.privacy_accept_pre')}{' '}
                                            <a
                                                href="/manuals/privacy_policy-v1.pdf"
                                                download
                                                className="text-primary hover:underline font-medium"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                {t('register_page.privacy_link')}
                                            </a>
                                            {' '}{t('register_page.privacy_accept_post')}
                                        </p>
                                    </div>
                                    {errors.agree_with_privacy && (
                                        <p className="text-red-500">{errors.agree_with_privacy.message}</p>
                                    )}
                                </div>
                            </div>
                        )
                    }

                    {
                        currentStep === 3 && (
                            <div>
                                <div className={'flex flex-col justify-center w-full'}>
                                    <img src={check} alt={'Logo for connect care mint green'} className={'max-h-16 mx-auto'}/>

                                    <h2 className={'text-2xl font-bold text-center mt-4 text-green-600'}>
                                        {t('auth.register_customer.success_title')}
                                    </h2>

                                    <p className={'mt-4 text-center text-lg'}>
                                        {t('auth.register_customer.success_message')}
                                    </p>

                                    <p className={'mt-2 text-center text-gray-600'}>
                                        {t('auth.register_customer.success_cta_message')}
                                    </p>
                                </div>
                            </div>
                        )
                    }

                    <div className={'mt-4 mb-1'}>
                        {
                            currentStep === 2 ?
                                <PrimaryButton type="submit">
                                    {t('login.cta_register')}
                                </PrimaryButton>
                            :
                                <PrimaryButton onClick={() => {
                                    if(currentStep === 3 && (user === null || user === undefined)) {
                                        return
                                    }

                                    progressSteps()
                                }}>
                                    {
                                        currentStep === 3 && user !== null && user !== undefined && (
                                            t('login.cta_dashboard')
                                        )
                                    }

                                    {
                                        currentStep === 3 && (user === null || user === undefined) && (
                                            t('auth.register_customer.preparing_account')
                                        )
                                    }

                                    {
                                        currentStep !== 3 && (
                                            t('login.cta_next')
                                        )
                                    }
                                </PrimaryButton>

                        }
                        {
                            currentStep === 2 && (
                                <PrimaryButton outlined={true} onClick={() => setCurrentStep(currentStep - 1)} className={'mt-2'}>
                                    {t('login.cta_previous')}
                                </PrimaryButton>
                            )
                        }
                    </div>
                </form>
            </BaseCard>
        </div>
    )
}